import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bot, FileText, RefreshCw, Send, Smartphone, Sparkles } from 'lucide-react';
import {
  buildCommunicationMessageFormData,
  fetchProspectLeadWorkspace,
  generateConversationAIDraft,
  hasCommunicationMedia,
  improveConversationAIMessage,
  markProspectConverted,
  sendConversationMessage,
  sendConversationWhatsAppMessage,
  sendConversationWhatsAppTemplate,
  sendProspectRegistrationLink,
  triggerProspectAutoReply,
  updateProspectLeadAIMode,
  updateProspectLeadStage,
} from './api';
import { socket, socketAuthPayload } from '../../admin/chat/socket';
import MessageBubble from './MessageBubble';
import MediaMessageControls from './MediaMessageControls';

function compactError(err, fallback) {
  const payload = err?.response?.data;
  return payload?.message || payload?.reason || payload?.error || err?.message || fallback;
}

function windowLabel(windowState = {}) {
  const bucket = windowState?.bucket;
  if (bucket === 'within_24') return 'WA libre <=24h';
  if (bucket === '24_48') return '24-48h: plantilla';
  if (bucket === '48_72') return '48-72h: plantilla';
  return '>72h: plantilla';
}

function templateClass(template, selected) {
  if (!template.available) return 'border-slate-200 bg-slate-50 text-slate-400';
  if (selected) return 'border-[#B1336E] bg-[#B1336E]/10 text-[#B1336E]';
  return 'border-slate-200 bg-white text-slate-700 hover:border-[#B1336E]/50';
}

export default function ProspectLeadMiniChat({ lead, onLeadUpdated, onWorkspaceLoaded }) {
  const [workspaceLead, setWorkspaceLead] = useState(lead || null);
  const [conversation, setConversation] = useState(lead?.conversation || null);
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [whatsappWindow, setWhatsappWindow] = useState(lead?.whatsapp_window || null);
  const [events, setEvents] = useState([]);
  const [channel, setChannel] = useState('whatsapp');
  const [selectedTemplateId, setSelectedTemplateId] = useState('prospect_followup');
  const [draft, setDraft] = useState('');
  const [mediaAttachment, setMediaAttachment] = useState(null);
  const [aiDraftMeta, setAiDraftMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiWorking, setAiWorking] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);

  const activeLead = workspaceLead || lead;
  const conversationId = conversation?.id || activeLead?.conversation_id;
  const freeformAvailable = Boolean(whatsappWindow?.freeform_available);
  const templatesRequired = Boolean(whatsappWindow?.templates_required) || !freeformAvailable;
  const availableTemplates = useMemo(() => templates.filter(item => item.available), [templates]);
  const selectedTemplate = useMemo(
    () => templates.find(item => item.id === selectedTemplateId) || availableTemplates[0] || templates[0] || null,
    [availableTemplates, selectedTemplateId, templates]
  );

  const loadWorkspace = useCallback(async () => {
    if (!lead?.id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchProspectLeadWorkspace(lead.id, { limit: 80 });
      setWorkspaceLead(data.lead || lead);
      setConversation(data.conversation || data.lead?.conversation || lead.conversation || null);
      setMessages(data.messages || []);
      setWhatsappWindow(data.whatsapp_window || data.lead?.whatsapp_window || null);
      setTemplates(data.templates?.templates || []);
      setEvents(data.events || []);
      if (data.whatsapp_window?.freeform_available) {
        setChannel(prev => (prev === 'template' ? 'whatsapp' : prev));
      } else {
        setChannel('template');
      }
      onLeadUpdated?.(data.lead);
      onWorkspaceLoaded?.(data);
    } catch (err) {
      setError(compactError(err, 'No se pudo cargar el chat del prospecto.'));
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [lead, onLeadUpdated, onWorkspaceLoaded]);

  useEffect(() => {
    setWorkspaceLead(lead || null);
    setConversation(lead?.conversation || null);
    setMessages([]);
    setDraft('');
    setMediaAttachment(null);
    setAiDraftMeta(null);
    setError(null);
    if (lead?.id) loadWorkspace();
  }, [lead?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!conversationId) return undefined;
    const payload = socketAuthPayload({ conversation_id: conversationId });
    const join = () => socket.emit('join_conversation', payload);
    const handleMessage = eventPayload => {
      const message = eventPayload?.message || eventPayload;
      if (Number(message?.conversation_id) !== Number(conversationId)) return;
      setMessages(prev => {
        if (prev.some(item => item.id === message.id && item.channel === message.channel)) return prev;
        return [...prev, message];
      });
    };
    const handleLead = payload => {
      const nextLead = payload?.lead;
      if (!nextLead || Number(nextLead.id) !== Number(activeLead?.id)) return;
      setWorkspaceLead(nextLead);
      setWhatsappWindow(nextLead.whatsapp_window || whatsappWindow);
      onLeadUpdated?.(nextLead);
    };
    const refresh = payload => {
      if (payload?.conversation_id && Number(payload.conversation_id) !== Number(conversationId)) return;
      loadWorkspace();
    };

    join();
    socket.on('connect', join);
    socket.on('conversation_message_created', handleMessage);
    socket.on('conversation_action_completed', refresh);
    socket.on('communication_lead_updated', handleLead);
    return () => {
      socket.off('connect', join);
      socket.off('conversation_message_created', handleMessage);
      socket.off('conversation_action_completed', refresh);
      socket.off('communication_lead_updated', handleLead);
      socket.emit('leave_conversation', payload);
    };
  }, [activeLead?.id, conversationId, loadWorkspace, onLeadUpdated, whatsappWindow]);

  const aiPayload = useCallback(() => ({
    source: 'prospect_lead_mini_chat',
    target_type: 'external_whatsapp',
    intent_hint: activeLead?.current_intent || undefined,
    prospect_lead_id: activeLead?.id,
  }), [activeLead]);

  async function handleGenerateDraft() {
    if (!conversationId || aiWorking) return;
    setAiWorking(true);
    setError(null);
    try {
      const result = await generateConversationAIDraft(conversationId, aiPayload());
      const nextDraft = result?.draft;
      const nextText = (nextDraft?.draft_text || '').trim();
      if (!nextText) throw new Error('AI no devolvio un borrador utilizable.');
      setDraft(nextText);
      setAiDraftMeta(nextDraft);
      if (freeformAvailable) setChannel('whatsapp');
    } catch (err) {
      setError(compactError(err, 'No se pudo crear el borrador AI.'));
    } finally {
      setAiWorking(false);
    }
  }

  async function handleImproveDraft() {
    const text = draft.trim();
    if (!text || !conversationId || aiWorking) return;
    setAiWorking(true);
    setError(null);
    try {
      const result = await improveConversationAIMessage(conversationId, text, aiPayload());
      setDraft(result.content || text);
      setAiDraftMeta(null);
    } catch (err) {
      setError(compactError(err, 'No se pudo corregir el texto.'));
    } finally {
      setAiWorking(false);
    }
  }

  async function handleSend(event) {
    event.preventDefault();
    if (!conversationId || sending) return;
    setSending(true);
    setError(null);
    try {
      if (channel === 'template') {
        if (!selectedTemplate?.available) throw new Error(selectedTemplate?.disabled_reason || 'Selecciona una plantilla configurada.');
        const result = await sendConversationWhatsAppTemplate(conversationId, selectedTemplate.id);
        if (result?.lead) {
          setWorkspaceLead(result.lead);
          onLeadUpdated?.(result.lead);
        }
      } else if (channel === 'note') {
        const text = draft.trim();
        if (!text) throw new Error('Escribe una nota interna.');
        await sendConversationMessage(conversationId, {
          content: text,
          is_note: true,
          message_type: 'note',
          source: 'prospect_lead_mini_chat',
          prospect_lead_id: activeLead?.id,
        });
      } else {
        if (!freeformAvailable) throw new Error(whatsappWindow?.disabled_reason || 'La ventana WhatsApp esta cerrada. Usa plantilla.');
        const text = draft.trim();
        if (!text && !mediaAttachment) throw new Error('Escribe un mensaje o adjunta media.');
        const payload = {
          content: text,
          message_type: mediaAttachment?.type || 'text',
          media_file: mediaAttachment?.file,
          media_type: mediaAttachment?.type,
          source: 'prospect_lead_mini_chat',
          prospect_lead_id: activeLead?.id,
        };
        await sendConversationWhatsAppMessage(
          conversationId,
          hasCommunicationMedia(payload) ? buildCommunicationMessageFormData(payload) : payload
        );
      }
      setDraft('');
      setMediaAttachment(null);
      setAiDraftMeta(null);
      await loadWorkspace();
    } catch (err) {
      setError(compactError(err, 'No se pudo enviar el mensaje.'));
    } finally {
      setSending(false);
    }
  }

  async function handleStage(stage, status = 'open') {
    if (!activeLead?.id) return;
    setSending(true);
    setError(null);
    try {
      const result = stage === 'converted'
        ? await markProspectConverted(activeLead.id)
        : await updateProspectLeadStage(activeLead.id, { stage, status });
      setWorkspaceLead(result.lead || activeLead);
      onLeadUpdated?.(result.lead || activeLead);
      await loadWorkspace();
    } catch (err) {
      setError(compactError(err, 'No se pudo actualizar el prospecto.'));
    } finally {
      setSending(false);
    }
  }

  async function handleRegistration() {
    if (!activeLead?.id) return;
    setSending(true);
    setError(null);
    try {
      let result;
      if (freeformAvailable) {
        result = await sendProspectRegistrationLink(activeLead.id);
      } else {
        result = await sendConversationWhatsAppTemplate(conversationId, 'prospect_registration');
      }
      setWorkspaceLead(result.lead || activeLead);
      onLeadUpdated?.(result.lead || activeLead);
      await loadWorkspace();
    } catch (err) {
      setError(compactError(err, 'No se pudo enviar registro.'));
    } finally {
      setSending(false);
    }
  }

  async function handleLeadAI() {
    if (!activeLead?.id) return;
    setSending(true);
    setError(null);
    try {
      const result = await updateProspectLeadAIMode(activeLead.id, !activeLead.ai_auto_enabled);
      setWorkspaceLead(result.lead || activeLead);
      onLeadUpdated?.(result.lead || activeLead);
    } catch (err) {
      setError(compactError(err, 'No se pudo actualizar AI lead.'));
    } finally {
      setSending(false);
    }
  }

  async function handleAutoReply() {
    if (!activeLead?.id) return;
    setSending(true);
    setError(null);
    try {
      const result = await triggerProspectAutoReply(activeLead.id);
      setWorkspaceLead(result.lead || activeLead);
      onLeadUpdated?.(result.lead || activeLead);
      await loadWorkspace();
    } catch (err) {
      setError(compactError(err, 'El agente no pudo auto-responder.'));
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(event) {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    if (channel === 'template' || sending || aiWorking) return;
    if (!draft.trim() && !mediaAttachment) return;
    handleSend(event);
  }

  if (!lead) {
    return (
      <div className="flex h-full min-h-[520px] items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm font-semibold text-slate-500">
        Selecciona un prospecto para conversar sin salir del drawer.
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-[520px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
      <div className="border-b border-slate-200 bg-white p-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate text-sm font-black text-slate-950">{activeLead?.phone_number || `Lead #${activeLead?.id}`}</h3>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-black ${freeformAvailable ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                {windowLabel(whatsappWindow)}
              </span>
              <span className="rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-black text-purple-700">
                {activeLead?.current_intent || 'sin intent'}
              </span>
            </div>
            <p className="mt-1 line-clamp-1 text-xs font-semibold text-slate-500">
              {activeLead?.ai_summary || 'Chat comercial de WhatsApp ads.'}
            </p>
          </div>
          <button onClick={loadWorkspace} disabled={loading} className="rounded-lg bg-slate-100 p-2 text-slate-600 hover:bg-slate-200 disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          <button onClick={() => handleStage('possible_lead', 'open')} disabled={sending} className="rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-black text-blue-700 disabled:opacity-50">
            Posible lead
          </button>
          <button onClick={() => handleStage('qualified', 'qualified')} disabled={sending} className="rounded-full bg-purple-50 px-2.5 py-1 text-[10px] font-black text-purple-700 disabled:opacity-50">
            Calificado
          </button>
          <button onClick={handleRegistration} disabled={sending || (!freeformAvailable && !templates.some(item => item.id === 'prospect_registration' && item.available))} className="rounded-full bg-[#B1336E]/10 px-2.5 py-1 text-[10px] font-black text-[#B1336E] disabled:opacity-50">
            Enviar registro
          </button>
          <button onClick={() => handleStage('converted', 'converted')} disabled={sending} className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-black text-emerald-700 disabled:opacity-50">
            Convertido
          </button>
          <button onClick={() => handleStage('handoff', 'handoff')} disabled={sending} className="rounded-full bg-amber-50 px-2.5 py-1 text-[10px] font-black text-amber-700 disabled:opacity-50">
            Handoff
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
        {loading && !messages.length && (
          <div className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white p-4 text-xs font-semibold text-slate-500">
            <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Cargando chat...
          </div>
        )}
        {!loading && !messages.length && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-xs font-semibold text-slate-500">
            Sin mensajes recientes para este lead.
          </div>
        )}
        {messages.map(message => (
          <MessageBubble key={`${message.channel}-${message.id}`} message={message} currentUserType="admin" />
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="border-t border-slate-200 bg-white p-3">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          {freeformAvailable && (
            <button type="button" onClick={() => setChannel('whatsapp')} className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-black ${channel === 'whatsapp' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700'}`}>
              <Smartphone className="h-3.5 w-3.5" /> WhatsApp
            </button>
          )}
          {templatesRequired && (
            <button type="button" onClick={() => setChannel('template')} className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-black ${channel === 'template' ? 'bg-[#B1336E] text-white' : 'bg-[#B1336E]/10 text-[#B1336E]'}`}>
              <FileText className="h-3.5 w-3.5" /> Plantillas
            </button>
          )}
          <button type="button" onClick={() => setChannel('note')} className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-black ${channel === 'note' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}>
            Nota
          </button>
          <button type="button" onClick={handleGenerateDraft} disabled={aiWorking || sending || !conversationId} className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-purple-100 px-2.5 py-1.5 text-[11px] font-black text-purple-700 hover:bg-purple-200 disabled:opacity-50">
            <Bot className={`h-3.5 w-3.5 ${aiWorking ? 'animate-pulse' : ''}`} /> AI draft
          </button>
          <button type="button" onClick={handleImproveDraft} disabled={aiWorking || sending || !draft.trim() || channel === 'template'} className="inline-flex items-center gap-1.5 rounded-lg bg-sky-100 px-2.5 py-1.5 text-[11px] font-black text-sky-700 hover:bg-sky-200 disabled:opacity-50">
            <Sparkles className={`h-3.5 w-3.5 ${aiWorking ? 'animate-pulse' : ''}`} /> Corregir
          </button>
        </div>

        {channel === 'template' ? (
          <div className="space-y-2">
            <div className="grid gap-2 sm:grid-cols-2">
              {templates.map(template => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => template.available && setSelectedTemplateId(template.id)}
                  disabled={!template.available}
                  className={`rounded-xl border p-2 text-left text-xs font-bold disabled:cursor-not-allowed ${templateClass(template, selectedTemplate?.id === template.id)}`}
                  title={template.disabled_reason || template.description}
                >
                  <div>{template.label}</div>
                  <div className="mt-1 text-[10px] font-semibold opacity-75">{template.disabled_reason || template.description}</div>
                </button>
              ))}
            </div>
            <button type="button" onClick={handleSend} disabled={sending || !selectedTemplate?.available} className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#B1336E] px-3 py-2 text-xs font-black text-white hover:bg-[#9d2d62] disabled:opacity-50">
              <Send className="h-4 w-4" /> Enviar plantilla
            </button>
          </div>
        ) : (
          <form onSubmit={handleSend} className="space-y-2">
            {channel === 'whatsapp' && (
              <MediaMessageControls value={mediaAttachment} onChange={setMediaAttachment} disabled={sending || aiWorking || !freeformAvailable} compact />
            )}
            <textarea
              value={draft}
              onChange={event => setDraft(event.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending || aiWorking || (channel === 'whatsapp' && !freeformAvailable)}
              rows={2}
              placeholder={channel === 'note' ? 'Nota interna para admins...' : 'Responder al lead por WhatsApp...'}
              className="w-full resize-none rounded-xl border border-slate-200 px-3 py-2 text-xs outline-none focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/20 disabled:bg-slate-100"
            />
            {aiDraftMeta && (
              <div className="rounded-lg bg-purple-50 px-2 py-1 text-[10px] font-bold text-purple-700">
                Draft {aiDraftMeta.intent || 'AI'} {aiDraftMeta.confidence != null ? `${Math.round((aiDraftMeta.confidence || 0) * 100)}%` : ''}
              </div>
            )}
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-semibold text-slate-500">
                {channel === 'whatsapp' ? (freeformAvailable ? 'Enter envia. WhatsApp libre dentro de 24h.' : whatsappWindow?.disabled_reason) : 'Nota interna, no se envia a WhatsApp.'}
              </span>
              <button type="submit" disabled={sending || aiWorking || (!draft.trim() && !mediaAttachment)} className="inline-flex items-center gap-1.5 rounded-lg bg-[#B1336E] px-3 py-2 text-xs font-bold text-white hover:bg-[#9d2d62] disabled:opacity-50">
                <Send className="h-3.5 w-3.5" /> Enviar
              </button>
            </div>
          </form>
        )}

        <div className="mt-2 flex flex-wrap gap-1.5">
          <button type="button" onClick={handleLeadAI} disabled={sending} className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-black text-slate-600 disabled:opacity-50">
            {activeLead?.ai_auto_enabled ? 'AI lead ON' : 'AI lead OFF'}
          </button>
          <button type="button" onClick={handleAutoReply} disabled={sending} className="rounded-full bg-purple-50 px-2 py-1 text-[10px] font-black text-purple-700 disabled:opacity-50">
            Auto-responder seguro
          </button>
        </div>

        {events.length > 0 && (
          <div className="mt-2 max-h-16 overflow-y-auto rounded-lg bg-slate-50 p-2 text-[10px] font-semibold text-slate-500">
            {events.slice(0, 4).map(event => (
              <div key={event.id}>{event.event_type} - {event.created_at ? new Date(event.created_at).toLocaleString('es-PE') : ''}</div>
            ))}
          </div>
        )}

        {error && <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-2 text-xs font-semibold text-red-700">{error}</div>}
      </div>
    </div>
  );
}
